import css from './form.module.scss';

import { memo } from 'react';

import { ErrorPane } from 'core/ui/Error';
import { ActionsRow } from 'core/ui/Form';
import { useMessageModal } from 'core/ui/MessageModal';

import { SaveButton, CancelButton } from './controls';
import {
  TAbstractEntity,
  TAbstractNewEntity,
  TEditorConfig,
} from './interface';
import useEditor from './useEditor';

type TProps<
  TContainer extends TAbstractEntity | null,
  TEntity extends TAbstractEntity,
  TNewEntity extends TAbstractNewEntity,
> = {
  container: TContainer;
  entity: TEntity | TNewEntity;
  onResult: (entity: TEntity | null) => void;
  mode: 'create' | 'change';
  config: TEditorConfig<TContainer, TEntity, TNewEntity>;
};

function EditorForm<
  TContainer extends TAbstractEntity | null,
  TEntity extends TAbstractEntity,
  TNewEntity extends TAbstractNewEntity,
>({
  mode,
  entity,
  container,
  onResult,
  config,
}: TProps<TContainer, TEntity, TNewEntity>) {
  const { editingEntity, onChange, onSave, onCancel, isFetching } = useEditor({
    entity,
    container,
    onResult,
    mode,
    config,
  });
  const { isEmpty, error } = config.onValidate(editingEntity);
  const { showError } = useMessageModal();

  const onSubmit = (event?: React.FormEvent) => {
    if (error) return;
    onSave(event).catch(showError);
  };

  return (
    <form className={css.form} onSubmit={onSubmit}>
      <config.Form
        entity={entity}
        editingEntity={editingEntity}
        container={container}
        onChange={onChange}
      />

      {error && <ErrorPane log={false} error={error} />}

      <ActionsRow>
        <SaveButton disabled={!!error || isEmpty} isLoading={isFetching} />
        <CancelButton onClick={onCancel} />
      </ActionsRow>
    </form>
  );
}

export default memo(EditorForm) as typeof EditorForm;
